import { Spinner } from "@chakra-ui/react";
import { Checkbox } from "components/RFQ/Checkbox";
import { useState } from "react";
import { revokeSubmission, submitSubmission } from "../../submission-api";
import {
	LONG_DATE_TIME_FORMAT,
	SubmissionDeadline,
} from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import { RFX_SUBMISSION_STATUS } from "../../constants";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";

const { Button } = require("newui/components/Button");
const { CustomModal } = require("components/RFQ/Modal/CustomModal");

interface SubmitModalProps {
	companyId?: number;
	guestEmail?: string;
	isGuest?: boolean;
	showDialog: boolean;
	afterRevokeSubmissionHandler: (result: boolean) => void;
	afterSubmissionHandler: (submissionDate: string | null) => void;
	onClose: () => void;
	saveSubmissionHandler: (
		currentTab: string,
		nextTab: string,
		isSubmitting: boolean
	) => Promise<boolean>;
}

export const SubmitModal: React.FC<SubmitModalProps> = ({
	companyId,
	guestEmail = "",
	isGuest = false,
	showDialog,
	afterRevokeSubmissionHandler,
	afterSubmissionHandler,
	onClose,
	saveSubmissionHandler,
}) => {
	const [checked, setChecked] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const { isOnboarding, rfxData, hasSubmitted, rfxSubmissionData } =
		useRfxSubmissionData();

	const getAddendumURL = (): string => {
		const currentURL = window.location.href;
		const pathParts = currentURL.split("/");
		pathParts[pathParts.length - 1] = "addendum";
		return pathParts.join("/");
	};

	const dialogProperty = {
		icon: `${hasSubmitted ? "alert-triangle" : "check-circle-blue"}`,
		bgColor: `${hasSubmitted ? "bg-yellow-200" : "bg-brand-secondary"}`,
		iconColor: "text-utility-brand-700",
		title: `${hasSubmitted ? "Retract and edit your submission" : "You're about to submit your response to this request"}`,
		description: hasSubmitted ? (
			<>
				<br />
				The deadline for this submission is:-
				<div className="py-4 text-lg font-bold">
					<SubmissionDeadline
						dateFormat={"long"}
						deadlineDateTime={new Date(rfxData?.submissionDeadline)}
						deadlineTimeZone={rfxData?.submissionTimezone}
						submissionStatus={RFX_SUBMISSION_STATUS.INPROGRESS}
						suppressTooltip
						twoLines={false}
					/>
				</div>
				<span className="font-semibold">
					Please ensure you resubmit before this time.
				</span>
			</>
		) : (
			<>
				Please review all details carefully to ensure all your information is
				accurate and complete.
				{isOnboarding && (
					<>
						<br />
						<br />
						Please confirm the following:
					</>
				)}
			</>
		),
	};

	const revokeHandler = (): void => {
		setSaving(true);
		revokeSubmission(rfxSubmissionData.id)
			.then((result) => {
				afterRevokeSubmissionHandler(result);
			})
			.finally(() => {
				setSaving(false);
				setChecked(false);
				onClose();
			});
	};

	const submitHandler = (): void => {
		setSubmitting(true);
		saveSubmissionHandler(null, null, true)
			.then((result) => {
				if (!result) {
					afterSubmissionHandler && afterSubmissionHandler(null);
					return;
				}
				setSubmitting(true);
				const submitPromise = submitSubmission(rfxSubmissionData.id, checked);

				submitPromise
					.then((submissionDate) => {
						afterSubmissionHandler && afterSubmissionHandler(submissionDate);
					})
					.catch(() => {
						afterSubmissionHandler && afterSubmissionHandler(null);
					});
			})
			.finally(() => {
				setChecked(false);
				setSaving(false);
				setSubmitting(false);
				onClose();
			});
	};

	const FooterComponent: React.FC = () => (
		<div className="flex w-full justify-between gap-2 text-utility-error-50">
			<Button
				onClick={onClose}
				btntype="base"
				className="!w-full"
				disabled={submitting || saving}
			>
				Cancel
			</Button>
			<Button
				className="flex !w-full gap-3"
				onClick={hasSubmitted ? revokeHandler : submitHandler}
				disabled={!checked || submitting || saving}
				data-testid="submit-button-modal"
			>
				{(submitting || saving) && <Spinner />}
				{saving
					? "Saving ..."
					: submitting
						? "Submitting ..."
						: `${hasSubmitted ? "Edit" : "Submit"} Now`}
			</Button>
		</div>
	);

	return (
		<CustomModal
			onClose={onClose}
			isOpen={showDialog}
			header={dialogProperty}
			footerComponent={<FooterComponent />}
			size="xl"
		>
			<div className="px-1 py-2">
				{
					// @ts-ignore
					<Checkbox
						label={
							hasSubmitted ? (
								<span>
									I acknowledge that by editing this submission, it is retracted
									until I resubmit.
								</span>
							) : isOnboarding ? (
								<span>
									I/We acknowledge that we have read and understood the
									requirements of this request and confirm that the information
									provided is accurate and complete.{" "}
								</span>
							) : (
								<span>
									I/We have read and acknowledge all{" "}
									<a
										target="_blank"
										href={`${getAddendumURL()}#rfx-container`}
										className="pe-1"
										rel="noreferrer"
									>
										addendums
									</a>
									published in this request.
								</span>
							)
						}
						className="text-sm"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setChecked(e.target.checked)
						}
						checked={checked}
						testId="addendum-checkbox"
					/>
				}
			</div>
		</CustomModal>
	);
};
